/**
 * Uppy 文件上传状态管理
 * 使用 Jotai 管理 Uppy 实例和上传状态
 */
import { atom, Setter } from 'jotai';
import Uppy, { UppyFile } from '@uppy/core';
import Transloadit from '@uppy/transloadit';
import { mediaService } from '../../services/mediaService';
import { MAX_SIZE_CONVERTIBLE, MAX_NUMBER_OF_FILES } from '../../constants/fileUploadConstants';
import { uppyBeforeFileAddedValidator } from '../../utils/fileValidation';
import { selectAtom } from 'jotai/utils';
import { UppyFileType, MinimalRequiredUppyFileType } from '../../types/uppy';
import { buildUppyFile } from '../../utils/buildUppyFile';


// Uppy 实例 atom
export const uppyInstanceAtom = atom<Uppy | null>(null);
export const filesMapAtom = atom<Record<string, MessageFileData>>({});

// 待上传文件队列 atom
export const pendingUploadMapAtom = atom<Record<string, UppyFileType>>({});

export const assemblyFileIdMapAtom = atom<Record<string, string>>({});

const uploadingStatus = ['preparing', 'uploading'];

export interface MessageFileData {
  id: string;
  name: string;
  type: string;
  size: number;
  extension: string;
  uploadStatus: 'queued' | 'preparing' | 'uploading' | 'aborted' | 'success' | 'error';
  progress: number | undefined;
  data: File | Blob;
  assemblyId: string | undefined;
  finalUrl: string | undefined;
  finalThumbnailUrl: string | undefined;
}


/**
 * 文件ID列表选择器
 * 只在文件列表结构（添加/删除文件）变化时通知订阅者
 * 文件内容（如上传进度）变化时不会触发更新
 */
export const fileIdsSelector = selectAtom(
  filesMapAtom,
  filesMap => Object.keys(filesMap),
  (prev, next) => {
    // 如果长度不同，列表肯定变化了
    if (prev.length !== next.length) return false;
    // 检查每个ID是否相同且顺序一致
    return prev.every((id, index) => id === next[index]);
  }
);

export const fileValuesSelector = selectAtom(
  filesMapAtom,
  filesMap => Object.values(filesMap),
  (prev, next) => {
    // 如果长度不同，列表肯定变化了
    if (prev.length !== next.length) return false;
    // 检查每个ID是否相同且顺序一致
    return prev.every((id, index) => id === next[index]);
  }
);

/**
 * 获取所有正在处理中的文件ID
 * 注意：'queued' 状态的文件不被视为正在处理中
 */
export const processingFileIdsSelector = selectAtom(
  filesMapAtom,
  filesMap => {
    return Object.entries(filesMap)
      .filter(([_, file]) =>
        file.uploadStatus === 'preparing' ||
        file.uploadStatus === 'uploading'
      )
      .map(([id]) => id);
  }
);

// 创建文件选择器函数，避免使用atomFamily
export const createFileSelector = (fileId: string) =>
  selectAtom(
    filesMapAtom,
    filesMap => filesMap[fileId],
    // 自定义比较函数，只关心我们需要展示的属性
    (prev, next) => {
      if (!prev && !next) return true;
      if (!prev || !next) return false;

      // 比较常见的属性
      return (
        prev.name === next.name &&
        prev.type === next.type &&
        prev.size === next.size &&
        prev.data === next.data &&
        prev.extension === next.extension &&
        prev.uploadStatus === next.uploadStatus &&
        prev.progress === next.progress &&
        prev.assemblyId === next.assemblyId &&
        prev.finalUrl === next.finalUrl &&
        prev.finalThumbnailUrl === next.finalThumbnailUrl
      );
    }
  );

// 创建一个更新单个文件的原子
export const updateSingleFileAtom = atom(
  null,
  (get, set, fileData: MessageFileData) => {
    const filesMap = get(filesMapAtom);
    set(filesMapAtom, {
      ...filesMap,
      [fileData.id]: fileData
    });
  }
);

/**
 * 创建文件数据并更新到状态中的工具函数
 * @param file Uppy文件对象
 * @param uploadStatus 上传状态
 * @param assemblyId 可选的assembly ID
 * @param finalUrl 可选的最终URL
 * @param finalThumbnailUrl 可选的缩略图URL
 */
export const createFileDataAndUpdate = (
  file: UppyFileType,
  uploadStatus: MessageFileData['uploadStatus'],
  assemblyId?: string,
  finalUrl?: string,
  finalThumbnailUrl?: string
) => {
  return (set: Setter) => {
    const fileData: MessageFileData = {
      id: file.id,
      name: file.name || '',
      type: file.type,
      size: file.size || 0,
      extension: '',
      uploadStatus,
      progress: uploadStatus === 'uploading' ? file.progress?.percentage : undefined,
      data: file.data,
      assemblyId,
      finalUrl,
      finalThumbnailUrl,
    };
    set(updateSingleFileAtom, fileData);
  };
};

// 创建一个删除单个文件的原子
export const removeSingleFileAtom = atom(
  null,
  (get, set, fileId: string) => {
    // 从文件映射中删除
    const filesMap = get(filesMapAtom);
    const newFilesMap = { ...filesMap };
    delete newFilesMap[fileId];
    set(filesMapAtom, newFilesMap);

    // 从待上传队列中移除（如果存在）
    const pendingUploadMap = get(pendingUploadMapAtom);
    if (fileId in pendingUploadMap) {
      const newPendingUploadMap = { ...pendingUploadMap };
      delete newPendingUploadMap[fileId];
      set(pendingUploadMapAtom, newPendingUploadMap);
    }
  }
);

// 放弃上传与处理单个文件的原子
export const abortSingleFileAtom = atom(
  null,
  (get, set, fileId: string) => {
    const uppyInstance = get(uppyInstanceAtom)
    if (!uppyInstance) {
      return
    }

    // 通知Uppy实例取消上传
    uppyInstance.removeFile(fileId);

    // 从状态中移除文件记录（这会同时从filesMapAtom和pendingUploadQueueAtom中移除）
    set(removeSingleFileAtom, fileId);
  }
);



// 专门用于获取文件ID的函数原子
export const getAssemblyFileIdFunctionAtom = atom((get) => {
  return (assemblyId: string, fileName: string) => {
    const assemblyFilesMap = get(assemblyFileIdMapAtom);
    const id = assemblyId + '_' + fileName;
    return assemblyFilesMap[id] || null;
  };
});

// 专门用于更新文件ID的原子
export const updateAssemblyFileIdAtom = atom(
  null, // 没有 getter
  (get, set, { assemblyId, fileName, fileId }: {
    assemblyId: string,
    fileName: string,
    fileId: string
  }) => {
    const assemblyFilesMap = get(assemblyFileIdMapAtom);
    const id = assemblyId + '_' + fileName;
    set(assemblyFileIdMapAtom, {
      ...assemblyFilesMap,
      [id]: fileId
    });
  }
);


export const deleteAssemblyFileAtom = atom(
  null, // 没有 getter
  (get, set, { assemblyId, fileName }: { assemblyId: string, fileName: string }) => {
    const assemblyFilesMap = get(assemblyFileIdMapAtom);
    const id = assemblyId + '_' + fileName;

    // 创建新的映射对象，排除要删除的项
    const newMap = { ...assemblyFilesMap };
    delete newMap[id];

    set(assemblyFileIdMapAtom, newMap);
  }
);




export const enqueueFilesAtom = atom(
  null,
  (get, set, files: MinimalRequiredUppyFileType[]) => {
    const uppyInstance = get(uppyInstanceAtom);
    if (!uppyInstance) {
      console.warn('添加文件到队列失败: Uppy实例不存在');
      return;
    }
    const uppyFiles = files.map(file => buildUppyFile(file, uppyInstance));
    const pendingUploadMap = get(pendingUploadMapAtom);

    //本地文件加入带上传队列
    const newPendingUploadMap = {
      ...pendingUploadMap,
      ...Object.fromEntries(uppyFiles.map(file => [file.id, file]))
    };
    set(pendingUploadMapAtom, newPendingUploadMap);

    //进入文件列表
    const filesMap = get(filesMapAtom);
    const newFilesMap = {
      ...filesMap,
      ...Object.fromEntries(uppyFiles.map(file => [file.id, {
        id: file.id,
        name: file.name || '',
        type: file.type,
        size: file.size || 0,
        extension: '',
        uploadStatus: 'queued',
        progress: undefined,
        data: file.data,
        assemblyId: undefined,
        finalUrl: undefined,
        finalThumbnailUrl: undefined,
      } as MessageFileData]))
    };
    set(filesMapAtom, newFilesMap);

    //执行对列排队
    set(processUploadQueueAtom)
  }


);


// 处理上传队列的原子
export const processUploadQueueAtom = atom(
  null,
  (get, set) => {
    const uppyInstance = get(uppyInstanceAtom);
    if (!uppyInstance) {
      console.warn('处理上传队列失败: Uppy实例不存在');
      return;
    }

    // 获取待上传队列
    let pendingUploadMap = get(pendingUploadMapAtom);
    if (Object.keys(pendingUploadMap).length === 0) {
      console.log('上传队列为空，无需处理');
      return;
    } else {
      console.log('上传对列不为空! ', pendingUploadMap)
    }

    // 检查是否有正在处理的文件（不包括队列中的文件）
    // 只检查 'preparing' 和 'uploading' 状态的文件，'queued' 状态的文件不算作正在处理
    const currentFilesMap = get(filesMapAtom);
    console.log('currentFilesMap', currentFilesMap)
    const processingFiles = Object.values(currentFilesMap).filter(file =>
      uploadingStatus.includes(file.uploadStatus) &&
      !(file.id in pendingUploadMap)
    );

    console.log('processingFiles', processingFiles)

    if (processingFiles.length > 0) {
      console.log('有文件正在处理中，暂不处理队列', processingFiles.map(f => f.id));

      // 设置一个定时器，定期检查是否可以处理队列
      const checkInterval = setInterval(() => {
        const currentFilesMap = get(filesMapAtom);
        const currentProcessingFiles = Object.values(currentFilesMap).filter(file =>
          (file.uploadStatus === 'preparing' || file.uploadStatus === 'uploading') &&
          !(file.id in pendingUploadMap)
        );

        if (currentProcessingFiles.length === 0) {
          clearInterval(checkInterval);
          // 所有文件处理完成，尝试处理队列
          set(processUploadQueueAtom);
        }
      }, 1000); // 每秒检查一次

      // 设置超时，防止无限等待
      setTimeout(() => {
        clearInterval(checkInterval);
        console.log('等待处理中的文件超时，尝试处理队列');
        set(processUploadQueueAtom);
      }, 30000); // 最多等待30秒

      return;
    }

    console.log('开始处理上传队列，队列长度:', Object.keys(pendingUploadMap).length);

    Object.values(pendingUploadMap).forEach(user => {
      if (user.name === undefined) {
        user.name = '';
      }
    });
    uppyInstance.addFiles(Object.values(pendingUploadMap as any))
    set(pendingUploadMapAtom, {});
  }
);

export const initUppyAtom = atom(
  (get) => get(uppyInstanceAtom),
  (get, set, options?: { autoProceed?: boolean, maxNumberOfFiles?: number }) => {

    const existingInstance = get(uppyInstanceAtom);
    if (existingInstance) {
      return existingInstance;
    }
    const autoProceed = options?.autoProceed ?? true;

    // const autoProceed = false;
    const maxNumberOfFiles = options?.maxNumberOfFiles ?? MAX_NUMBER_OF_FILES;
    // 创建 Uppy 实例
    const uppyInstance = new Uppy({
      id: 'chatUppy',
      autoProceed,
      allowMultipleUploadBatches: true,
      debug: process.env.NODE_ENV !== 'production',
      restrictions: {
        // 设置最大值，在onBeforeFileAdded中会根据文件类型进行更精细的限制
        // allowedFileTypes: ALL_ALLOWED_MIME_TYPES,
        maxFileSize: MAX_SIZE_CONVERTIBLE, // 36MB
        maxNumberOfFiles,

      },
      onBeforeFileAdded: uppyBeforeFileAddedValidator
    });


    const tranloaditPuginInstall = () => {
      const transLoaditPlugin = uppyInstance.getPlugin('Transloadit')
      if (transLoaditPlugin) {
        console.log('=========== Transloadit Installed =========')
        return;
      } else {
        console.log('=========== Transloadit not Installed, Start to install =========')
        // 配置 Transloadit 插件
        uppyInstance.use(Transloadit, {
          service: 'https://api2.transloadit.com',
          waitForEncoding: true,
          alwaysRunAssembly: true,
          waitForMetadata: true,
          assemblyOptions: async () => {
            // 从后端获取 Transloadit 配置和签名
            try {
              // 使用 mediaService 获取参数和签名
              const response = await mediaService.getTransloaditParams();

              // 直接返回后端提供的参数和签名
              return {
                params: response.params,
                signature: response.signature
              };
            } catch (error) {
              console.error('获取 Transloadit 参数失败:', error);
              throw error; // 让 Uppy 处理错误
            }
          }
        });
      }
    }

    tranloaditPuginInstall()


    // 使用全局的createFileDataAndUpdate函数

    // 获取单文件删除函数
    const abortFile = (fileId: string) => {
      set(abortSingleFileAtom, fileId);
    };


    uppyInstance.on('upload', (uploadId, files: UppyFileType[]) => {
      console.log('!!!!!!!!:upload !!!!!!!!:', uploadId, files);
    });

    // 添加错误处理
    uppyInstance.on('error', (error: any) => {
      if (error.assembly) {
        console.log(`Assembly ID ${error.assembly.assembly_id} failed!`);
        console.log(error.assembly);
      }

      console.error('Uppy 错误:', error);
    });

    uppyInstance.on('upload-error', (file, error) => {
      console.error('上传错误:', error);
      if (file) {
        createFileDataAndUpdate(file, 'error')(set);
      }
    });

    uppyInstance.on('upload-start', () => {
      console.log('=============上传开始===========');
    });

    // 1 stage
    // no needed because of files-added
    // uppyInstance.on('file-added', (file) => {
    //   console.log('文件已添加:', file);
    //   updateFile(file, 'uploading');
    // });

    uppyInstance.on('files-added', (files) => {
      console.log('批量文件已添加:', files);
      files.forEach((file) => {
        createFileDataAndUpdate(file, 'uploading')(set);
      })
    });

    uppyInstance.on('file-removed', (file) => {
      console.log('文件已移除:', file);
      abortFile(file.id);
    });

    // 3 stage
    // per file upload progress
    // fileLocalId, progress: {bytesUploaded: number, bytesTotal: number, percentage: number}
    uppyInstance.on('upload-progress', (file, progress) => {
      if (!file) {
        console.warn('upload-progress falsy file')
        return
      }
      createFileDataAndUpdate(file, 'uploading')(set);
      console.log('上传进度:', file.id, file.name, progress.percentage);
    });

    // 4 stage
    // per file upload result
    //
    uppyInstance.on('upload-success', (file, response) => {
      console.log('上传成功:', file, response);
      if (!file) {
        console.warn('upload-success falsy file')
        return
      }
      createFileDataAndUpdate(file, 'preparing')(set);
    });
    uppyInstance.on('upload-error', (file, error, response) => {
      console.error('文件上传错误:', file);
      if (!file) {
        console.warn('upload-error falsy file')
        return
      }
      createFileDataAndUpdate(file, 'error')(set);
      console.error(`error:`, error)
      console.error(`response:`, response)
    });

    // 2 stage
    // batch got LocalfileIds with assembly id
    uppyInstance.on('transloadit:assembly-created', (assembly, fileIDs) => {
      console.group('Created', assembly.assembly_id, 'for files:');
      for (const id of fileIDs) {
        console.log(uppyInstance.getFile(id).name);
        const fileAtom = get(filesMapAtom)[id]
        set(updateAssemblyFileIdAtom, { assemblyId: assembly.assembly_id, fileName: fileAtom.name ?? '', fileId: id });
      }
      console.groupEnd();
    });

    uppyInstance.on('transloadit:upload', (file, assembly) => {
      console.log('Uploaded file to assembly', file, assembly);
      const getFileId = get(getAssemblyFileIdFunctionAtom);
      const messageFileId = getFileId(assembly.assembly_id, file.original_name);
      if (!messageFileId) {
        console.warn('upload falsy messageFileId')
        return
      }
      const fileAtom = get(filesMapAtom)[messageFileId]
      set(updateSingleFileAtom, {
        ...fileAtom,
        uploadStatus: 'preparing'
      });
    });
    uppyInstance.on('transloadit:result', (stepName, result, assembly) => {
      console.log('Got result from assembly', assembly, 'for step', stepName, result);
      const getFileId = get(getAssemblyFileIdFunctionAtom);
      const messageFileId = getFileId(assembly.assembly_id, result.original_name);
      if (!messageFileId) {
        console.warn('result falsy messageFileId')
        return
      }
      const fileAtom = get(filesMapAtom)[messageFileId]

      if (stepName === 'mini-thumbnails') {
        set(updateSingleFileAtom, {
          ...fileAtom,
          uploadStatus: 'preparing',
          finalThumbnailUrl: result.url
        })
      } else {
        set(updateSingleFileAtom, {
          ...fileAtom,
          uploadStatus: 'preparing',
          finalUrl: result.url
        })
      }
    });

    uppyInstance.on('transloadit:complete', (assembly) => {
      console.log('Assembly complete: ', assembly);
      const getFileId = get(getAssemblyFileIdFunctionAtom);

      Object.entries(assembly.results).forEach(([, value]) => {
        for (const file of value) {
          const messageFileId = getFileId(assembly.assembly_id, file.original_name);
          if (!messageFileId) {
            console.warn('complete falsy messageFileId')
            continue
          }
          const fileAtom = get(filesMapAtom)[messageFileId]
          set(updateSingleFileAtom, {
            ...fileAtom,
            uploadStatus: 'success'
          });
        }
      });
      // 检查是否有待处理的队列
      setTimeout(() => {
        set(processUploadQueueAtom);
      }, 500); // 稍微延迟，确保状态已更新
    });

    uppyInstance.on('complete', (result) => {
      if (!result) {
        console.warn('complete falsy result')
        return
      }
      if (result.successful) {
        result.successful.forEach((file) => {
          const fileAtom = get(filesMapAtom)[file.id]
          set(updateSingleFileAtom, {
            ...fileAtom,
            uploadStatus: 'success'
          });
        });
      }


      if (result.failed) {
        result.failed.forEach((file) => {
          const fileAtom = get(filesMapAtom)[file.id]
          set(updateSingleFileAtom, {
            ...fileAtom,
            uploadStatus: 'error'
          });
          uppyInstance.retryUpload(file.id);
        });
      }
      console.log('上传完成:', result);


      // 检查是否有待处理的队列
      setTimeout(() => {
        set(processUploadQueueAtom);
      }, 500); // 稍微延迟，确保状态已更新
    });

    set(uppyInstanceAtom, uppyInstance);
    return uppyInstance;
  });



// 清理 Uppy 实例的 atom
export const cleanupUppyAtom = atom(
  null, // 读取器不需要返回值
  (get, set) => {
    const currentInstance = get(uppyInstanceAtom);
    if (currentInstance) {
      currentInstance.cancelAll();
      // 注意：在Uppy 3.x中，不需要手动调用close()
      set(uppyInstanceAtom, null);
    }
    set(filesMapAtom, {});
    set(assemblyFileIdMapAtom, {});
    set(pendingUploadMapAtom, {});
  }
);


// 判断是否还有上传中, 或者缓存的文件区. 
export const isUploadingAtom = atom((get) => {
  const filesMap = get(filesMapAtom);
  return Object.values(filesMap).some(file =>
    uploadingStatus.includes(file.uploadStatus)
  ) || Object.keys(get(pendingUploadMapAtom)).length > 0;
});
